import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  NotificationDto,
  NotificationType,
  NotificationCenterService,
  ReferenceType,
} from '../../../../api/gen';

@Component({
  selector: 'app-notification-item',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    RouterLink,
    DatePipe,
  ],
  templateUrl: './notification-item.component.html',
  styleUrls: ['./notification-item.component.scss'],
})
export class NotificationItemComponent {
  readonly notification = input.required<NotificationDto>();
  readonly notificationRead = output<NotificationDto>();
  private readonly notificationCenterService = inject(
    NotificationCenterService,
  );
  private readonly router = inject(Router);
  protected readonly markingAsRead = signal(false);
  protected readonly readFailed = signal(false);
  protected readonly markAsReadTooltip = $localize`Als gelesen markieren.`;
  protected readonly deliveryFailed = computed(
    () => this.notification().type === NotificationType.MessageDeliveryFailed,
  );
  protected readonly presentation = computed(() => {
    const notification = this.notification();
    switch (notification.type) {
      case NotificationType.AttachmentCreated:
        return {
          icon: 'attach_file',
          title: $localize`:@@notificationAttachmentTitle:Neuer Anhang`,
          text: $localize`:@@notificationAttachmentText:Ein neuer Kundenanhang ist verfügbar.`,
        };
      case NotificationType.WorkflowSubmitted:
        return {
          icon: 'account_tree',
          title: $localize`:@@notificationWorkflowSubmittedTitle:Workflow eingereicht`,
          text: $localize`:@@notificationWorkflowSubmittedText:Ein neuer Workflow wurde eingereicht.`,
        };
      case NotificationType.WorkflowStepNotification:
        return {
          icon: 'account_tree',
          title: $localize`:@@notificationWorkflowStepTitle:Workflow: Prüfung erforderlich`,
          text: $localize`:@@notificationWorkflowStepText:Ein Workflow-Schritt benötigt Ihre Aufmerksamkeit.`,
        };
      case NotificationType.CustomerTaskCompleted:
        return {
          icon: 'task_alt',
          title: $localize`:@@notificationTaskCompletedTitle:Kundenaufgabe erledigt`,
          text: $localize`:@@notificationTaskCompletedText:Ein neues Kundenergebnis ist verfügbar.`,
        };
      case NotificationType.CustomerTaskReopened:
        return {
          icon: 'restart_alt',
          title: $localize`:@@notificationTaskReopenedTitle:Kundenaufgabe wieder geöffnet`,
          text: $localize`:@@notificationTaskReopenedText:Der Kunde hat die Aufgabe wieder geöffnet.`,
        };
      case NotificationType.AgentTasksCreated:
        return {
          icon: 'auto_awesome',
          title: $localize`:@@notificationAiTasksTitle:Neue Aufgaben aus dem Video-Termin`,
          text: notification.context?.taskCount
            ? $localize`:@@notificationAiTasksCount:${notification.context.taskCount} neue Aufgaben wurden erstellt.`
            : $localize`:@@notificationAiTasksText:Neue Aufgaben wurden aus der Gesprächszusammenfassung erstellt.`,
        };
      case NotificationType.MessageDeliveryFailed:
        return {
          icon: 'error_outline',
          title:
            notification.context?.channel === 'Sms'
              ? $localize`:@@notificationSmsFailedTitle:SMS nicht zugestellt`
              : $localize`:@@notificationEmailFailedTitle:E-Mail nicht zugestellt`,
          text: $localize`:@@notificationDeliveryFailedText:Bitte prüfen Sie die Zustellung und die Empfängerdaten.`,
        };
      default:
        return {
          icon: 'notifications_none',
          title: $localize`Benachrichtigung.`,
          text: $localize`Unbekannte Benachrichtigung.`,
        };
    }
  });
  protected readonly text = computed(() =>
    this.notification().type === NotificationType.AgentTasksCreated
      ? this.presentation().text
      : this.notification().context?.message || this.presentation().text,
  );
  protected readonly referenceLink = computed(() => {
    const { referenceId, referenceType, context, type } = this.notification();
    if (!referenceId) return null;
    switch (referenceType) {
      case ReferenceType.Attachment:
        return {
          url: this.router.createUrlTree(
            ['/video-appointments/navigate-to-attachment'],
            { queryParams: { attachmentId: referenceId } },
          ),
          label: $localize`:@@notificationOpenAttachment:Anhang öffnen`,
        };
      case ReferenceType.Inquiry:
        return {
          url: this.router.createUrlTree(['/cases', referenceId], {
            queryParams:
              type === NotificationType.AgentTasksCreated
                ? { section: 'tasks' }
                : {},
          }),
          label:
            type === NotificationType.AgentTasksCreated
              ? $localize`:@@notificationOpenTasks:Aufgaben öffnen`
              : $localize`:@@notificationOpenCase:Fall öffnen`,
        };
      case ReferenceType.WorkflowRun:
        return {
          url: this.router.createUrlTree([
            '/workflows/runs',
            referenceId,
            ...(type === NotificationType.WorkflowStepNotification
              ? ['in-progress']
              : []),
          ]),
          label:
            type === NotificationType.WorkflowSubmitted
              ? $localize`:@@notificationOpenSubmission:Einsendung öffnen`
              : $localize`:@@notificationOpenWorkflow:Workflow öffnen`,
        };
      case ReferenceType.CaseTask:
        return context?.caseId
          ? {
              url: this.router.createUrlTree(
                ['/cases', context.caseId, 'tasks', referenceId],
                {
                  queryParams:
                    type === NotificationType.CustomerTaskCompleted
                      ? { result: 'true' }
                      : {},
                },
              ),
              label:
                type === NotificationType.CustomerTaskCompleted
                  ? $localize`:@@notificationOpenResult:Ergebnis öffnen`
                  : $localize`:@@notificationOpenTask:Aufgabe öffnen`,
            }
          : null;
      case ReferenceType.MessageDelivery:
        return {
          url: this.router.createUrlTree([
            '/notifications/deliveries',
            referenceId,
          ]),
          label: $localize`:@@notificationOpenDelivery:Zustellung prüfen`,
        };
      default:
        return null;
    }
  });

  onNotificationReadClick(): void {
    const notificationId = this.notification().id;
    if (!notificationId || this.markingAsRead()) return;
    this.markingAsRead.set(true);
    this.readFailed.set(false);
    this.notificationCenterService
      .notificationCenterV2NotificationPatch({
        read: true,
        notificationId,
      })
      .subscribe({
        next: (notification) => this.notificationRead.emit(notification),
        error: () => {
          this.markingAsRead.set(false);
          this.readFailed.set(true);
        },
      });
  }
}
